import type {
  SplitViewerConstructorOptions,
  BoundingBox,
  CVTilesLayer,
} from '@aus-platform/cesium';
import { SplitViewer, ZoomMode } from '@aus-platform/cesium';
import {
  Cartographic,
  Rectangle,
  Color,
  Cartesian3,
  type Cesium3DTileset,
} from 'cesium';
import CesiumNavigation, {
  type NavigationOptions,
} from 'cesium-navigation-es6'; // eslint-disable-line import/default
import { ColorCodes } from '@aus-platform/design-system';
import { baseMapImageryProvider } from './constants';
import type {
  ICesiumSplitViewer,
  SplitViewerAddMapLayerParams,
} from './interfaces';
import { CesiumLayerManager } from './managers';
import type { SwipeMap3DBaseLayers, SwipeMapLayer } from 'src/swipe-map-3d';
import { getLayerMetadata } from 'map-3d/components';
import type { SiteListItem } from '../api';
import { LayerType } from '../api';
import { type MapLayer } from './types';

export class CesiumSplitViewerProxy
  extends SplitViewer
  implements ICesiumSplitViewer
{
  private _leftViewerLayerManager: CesiumLayerManager;
  private _rightViewerLayerManager: CesiumLayerManager;

  constructor(options: SplitViewerConstructorOptions) {
    super(options);

    this._leftViewerLayerManager = new CesiumLayerManager(this.viewerLeft);
    this._rightViewerLayerManager = new CesiumLayerManager(this.viewerRight);

    if (this.viewerRight.viewer && this.viewerLeft.viewer) {
      const navigationOptions: NavigationOptions = {
        enableCompass: true,
        enableZoomControls: false,
        enableDistanceLegend: false,
        enableCompassOuterRing: true,
      };

      new CesiumNavigation(this.viewerRight.viewer, navigationOptions);
      new CesiumNavigation(this.viewerLeft.viewer, navigationOptions);
    }
  }

  // Centralized camera helpers
  private flyToRectangle(rect: Rectangle) {
    this.viewerRight.viewer?.camera.flyTo({ destination: rect, duration: 1 });
    this.viewerLeft.viewer?.camera.flyTo({ destination: rect, duration: 1 });
  }

  private flyToDestination(destination: Cartesian3) {
    this.viewerRight.viewer?.camera.flyTo({ destination, duration: 1 });
    this.viewerLeft.viewer?.camera.flyTo({ destination, duration: 1 });
  }

  addBaseImageryLayers(): SwipeMap3DBaseLayers | undefined {
    if (!this.viewerLeft.viewer || !this.viewerRight.viewer) {
      return;
    }

    const leftBaseLayer =
      this.viewerLeft.viewer?.imageryLayers.addImageryProvider(
        baseMapImageryProvider,
        0,
      );

    const rightBaseLayer =
      this.viewerRight.viewer?.imageryLayers.addImageryProvider(
        baseMapImageryProvider,
        0,
      );

    this.viewerLeft.viewer.scene.globe.baseColor = Color.fromCssColorString(
      ColorCodes.MapBackground,
    );
    this.viewerRight.viewer.scene.globe.baseColor = Color.fromCssColorString(
      ColorCodes.MapBackground,
    );

    return {
      left: leftBaseLayer,
      right: rightBaseLayer,
    };
  }

  // Zoom Controls.
  zoomToLayer(layer: SwipeMapLayer) {
    // Prefer tileset-based zoom for point clouds
    if (layer.type === LayerType.PointCloud) {
      const leftTileset = this._leftViewerLayerManager?.selectPointCloudLayer(
        layer.id,
      );
      const rightTileset = this._rightViewerLayerManager?.selectPointCloudLayer(
        layer.id,
      );
      const tileset = leftTileset ?? rightTileset;

      if (tileset) {
        const layerManager = leftTileset
          ? this._leftViewerLayerManager
          : this._rightViewerLayerManager;

        const rectangle = layerManager.getPointCloudRectangle(tileset);

        if (rectangle) {
          this.flyToRectangle(rectangle);
        }
        return;
      }
    }

    // Fallback to bounds-based zoom (works for all non-point-cloud layers)
    const properties = getLayerMetadata(layer);

    const destination: Cartographic | null = properties?.bounds
      ? Rectangle.center(Rectangle.fromDegrees(...properties.bounds))
      : null;

    if (!destination) {
      return;
    }

    let altitudeAtDestination =
      this.viewerRight.viewer?.scene.globe.getHeight(destination) ?? 0;

    // altitudeAtDestination will be negative if terrain is not available.
    altitudeAtDestination =
      altitudeAtDestination < 0 ? 5000 : altitudeAtDestination + 5000;

    destination.height = altitudeAtDestination;

    this.flyToDestination(Cartographic.toCartesian(destination));
  }

  zoomToSite(site: SiteListItem) {
    const destination = Cartesian3.fromDegrees(
      site.longitude,
      site.latitude,
      5000,
    );

    if (!destination) {
      return;
    }

    this.viewerRight.viewer?.camera.flyTo({
      destination,
      duration: 1.5,
    });
  }

  zoomOut() {
    this.zoom(ZoomMode.ZoomOut);
  }

  zoomIn() {
    this.zoom(ZoomMode.ZoomIn);
  }

  reset(bounds?: BoundingBox) {
    if (bounds) {
      this.flyToBounds(bounds);
    } else {
      this.flyHome(1.5);
    }
  }

  removeAllImageryLayersExceptBase(isLeftViewer = true) {
    if (isLeftViewer) {
      this._leftViewerLayerManager?.removeAllImageryLayersExceptBase();
    } else {
      this._rightViewerLayerManager?.removeAllImageryLayersExceptBase();
    }
  }

  async addMapLayer({
    isLeftViewer = true,
    ...rest
  }: SplitViewerAddMapLayerParams) {
    if (isLeftViewer) {
      return await this._leftViewerLayerManager?.addMapLayer(rest);
    } else {
      return await this._rightViewerLayerManager?.addMapLayer(rest);
    }
  }

  addTerrain(iterationId: string, terrainPath: string, isLeftViewer = true) {
    if (isLeftViewer) {
      this._leftViewerLayerManager.addTerrain(iterationId, terrainPath);
    } else {
      this._rightViewerLayerManager.addTerrain(iterationId, terrainPath);
    }
  }

  removeMapLayer(isLeftViewer = true, mapLayers: MapLayer[]) {
    if (isLeftViewer) {
      this._leftViewerLayerManager?.removeImageryLayers(mapLayers);
    } else {
      this._rightViewerLayerManager?.removeImageryLayers(mapLayers);
    }
  }

  resetTerrain(isLeftViewer = true) {
    if (isLeftViewer) {
      this._leftViewerLayerManager.resetTerrainProvider();
    } else {
      this._rightViewerLayerManager.resetTerrainProvider();
    }
  }

  removePointCloudLayer(tileset: Cesium3DTileset, isLeftViewer = true) {
    if (isLeftViewer) {
      this._leftViewerLayerManager?.removePointCloudLayer(tileset);
    } else {
      this._rightViewerLayerManager?.removePointCloudLayer(tileset);
    }
  }

  removeCadLayer(layer: CVTilesLayer, isLeftViewer = true) {
    if (isLeftViewer) {
      this._leftViewerLayerManager?.removeCadLayer(layer);
    } else {
      this._rightViewerLayerManager?.removeCadLayer(layer);
    }
  }

  removeAllPointCloudLayers(isLeftViewer = true) {
    if (isLeftViewer) {
      this._leftViewerLayerManager?.removeAllPointCloudLayers();
    } else {
      this._rightViewerLayerManager?.removeAllPointCloudLayers();
    }
  }

  get leftLayerManager() {
    return this._leftViewerLayerManager;
  }

  get rightLayerManager() {
    return this._rightViewerLayerManager;
  }
}
